﻿export interface CompassGhost {
    // Bearing in the same frame as `runwayHeading`.
    bearing: number;
    label: string;
    recipLabel: string;
    isBest?: boolean;
    isHovered?: boolean;
}

export interface CompassConfig {
    // Runway heading in degrees (0–360).
    runwayHeading: number;
    // Wind direction in degrees (0–360, where wind blows FROM).
    windDirection: number;
    // Wind speed in knots.
    windSpeed: number;
    // Gust speed in knots (optional).
    gustSpeed?: number;
    // Headwind component (knots) — positive = headwind, negative = tailwind.
    headwind: number;
    // Crosswind component (knots).
    crosswind: number;
    // Show numeric values (optional).
    showValues?: boolean;
    // Show gust extensions on component arrows (optional).
    showGustAsDashed?: boolean;
    // Show wind/headwind/crosswind bearing markers on the compass ring (optional).
    showHeadingOnCompass?: boolean;
    // Scale wind arrow length by wind speed (true, default) or keep it fixed-length (false).
    windArrowScaled?: boolean;
    // Theme for colors.
    theme: 'light' | 'dark';
    // Magnetic variation in degrees (positive = east). Used only for runway ID labels.
    magneticVariation?: number;
    // Explicit runway ID for the active end (e.g. "08L"). Overrides the heading-derived label when provided.
    activeRunwayId?: string;
    // Explicit runway ID for the reciprocal end (e.g. "26R"). Overrides the heading-derived label when provided.
    recipRunwayId?: string;
    // Other runway bearings drawn behind the selected runway.
    ghosts?: CompassGhost[];
    // Runway the component arrows/values belong to. Defaults to `runwayHeading`.
    componentHeading?: number;
}

const COLORS = {
    light: {
        bg: '#ffffff',
        ring: 'rgb(51 51 51 / 0.65)',
        tick: '#666666',
        text: '#333333',
        runwayActive: '#1a73e8',
        rwySurface: '#6b7280',
        rwyLabel: '#ffffff',
        rwyLabelDim: 'rgba(255,255,255,0.45)',
        headwind: '#7ad67f',
        tailwind: '#ef5350',
        crosswind: '#ffa726',
        wind: '#42a5f5'
    },
    dark: {
        bg: '#1e1e1e',
        ring: '#cccccc',
        tick: '#999999',
        text: '#e0e0e0',
        runwayActive: '#64b5f6',
        rwySurface: '#4b5563',
        rwyLabel: '#ffffff',
        rwyLabelDim: 'rgba(255,255,255,0.35)',
        headwind: '#7ad67f',
        tailwind: '#ef5350',
        crosswind: '#ffa726',
        wind: '#42a5f5'
    }
};

const ARROW_WIDTH = 3.5;
const WIND_ARROW_WIDTH = 3.5;
const WIND_SCALE_MAX_KT = 50;

function hdgToLabel(deg: number): string {
    const h = ((deg % 360) + 360) % 360;
    const n = Math.round(h / 10) % 36;
    return String(n === 0 ? 36 : n).padStart(2, '0');
}

// Converts rotated-frame coords back to world canvas coords
function r2w(cx: number, cy: number, rwyRad: number, rx: number, ry: number) {
    return {
        x: cx + rx * Math.cos(rwyRad) - ry * Math.sin(rwyRad),
        y: cy + rx * Math.sin(rwyRad) + ry * Math.cos(rwyRad)
    };
}

function arrow(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color: string, lineWidth: number = ARROW_WIDTH) {
    const dx = x2 - x1,
        dy = y2 - y1;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 2) {
        return;
    }
    const angle = Math.atan2(dy, dx);
    const headLen = Math.min(10, len * 0.35);
    x2 -= Math.cos(angle) * (lineWidth * 1.15);
    y2 -= Math.sin(angle) * (lineWidth * 1.15);

    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 7), y2 - headLen * Math.sin(angle - Math.PI / 7));

    ctx.lineTo(x2 - headLen * Math.cos(angle + Math.PI / 7), y2 - headLen * Math.sin(angle + Math.PI / 7));

    ctx.lineTo(x2, y2);
    ctx.lineTo(x2 - headLen * Math.cos(angle - Math.PI / 7), y2 - headLen * Math.sin(angle - Math.PI / 7));

    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.stroke();
    ctx.fillStyle = color;
    ctx.fill();
}


interface StripStyle {
    surface: string;
    threshold: string;
    label: string;
    recipLabel: string;
    alpha: number;
    labelAlpha?: number;
    labelHalo?: string;
    outline?: { color: string; dash?: number[] };
}

function drawRunwayStrip(
    ctx: CanvasRenderingContext2D,
    cx: number,
    cy: number,
    heading: number,
    rwyLen: number,
    rwyW: number,
    labels: { active: string; recip: string },
    style: StripStyle
) {
    ctx.save();
    ctx.globalAlpha = style.alpha;
    ctx.translate(cx, cy);
    ctx.rotate(((heading - 90) * Math.PI) / 180);

    ctx.fillStyle = style.surface;
    ctx.beginPath();
    ctx.roundRect(-rwyLen, -rwyW / 2, rwyLen * 2, rwyW, 2);
    ctx.fill();

    ctx.fillStyle = style.threshold;
    ctx.fillRect(-rwyLen, -rwyW / 2, 3, rwyW);

    const sW = 2, sGap = 5;
    const sH = rwyW - 12;
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    for (let i = 0; i < 2; i++) {
        const off = i * (sH / 2);
        ctx.fillRect(-rwyLen + sGap, sW + off, sH, sW);
        ctx.fillRect(rwyLen - sGap - sH, sW + off, sH, sW);
    }
    for (let i = 0; i < 2; i++) {
        const off = 6 + i * (sH / 2);
        ctx.fillRect(-rwyLen + sGap, sW - off, sH, sW);
        ctx.fillRect(rwyLen - sGap - sH, sW - off, sH, sW);
    }

    ctx.beginPath();
    ctx.setLineDash([8, 7]);
    ctx.moveTo(-rwyLen + 8, 0);
    ctx.lineTo(rwyLen - 8, 0);
    ctx.strokeStyle = 'rgba(255,255,255,0.3)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.setLineDash([]);

    if (style.outline) {
        ctx.beginPath();
        ctx.roundRect(-rwyLen, -rwyW / 2, rwyLen * 2, rwyW, 2);
        ctx.setLineDash(style.outline.dash ?? []);
        ctx.strokeStyle = style.outline.color;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.setLineDash([]);
    }

    ctx.globalAlpha = style.labelAlpha ?? style.alpha;
    const ends: [number, number, string, string][] = [
        [-rwyLen + 19, Math.PI / 2, labels.active, style.label],
        [rwyLen - 19, -Math.PI / 2, labels.recip, style.recipLabel]
    ];
    for (const [offset, rotation, text, color] of ends) {
        if (!text) {
            continue;
        }
        ctx.save();
        ctx.translate(offset, 0);
        ctx.rotate(rotation);
        // Merged labels ("09L/R") are wider than a single ID - shrink them to fit the strip.
        let fontSize = 11;
        ctx.font = `bold ${fontSize}px sans-serif`;
        while (fontSize > 6.5 && ctx.measureText(text).width > rwyW - 1) {
            fontSize -= 0.5;
            ctx.font = `bold ${fontSize}px sans-serif`;
        }
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        if (style.labelHalo) {
            ctx.strokeStyle = style.labelHalo;
            ctx.lineWidth = 2.5;
            ctx.lineJoin = 'round';
            ctx.strokeText(text, 0, 0);
        }
        ctx.fillStyle = color;
        ctx.fillText(text, 0, 0);
        ctx.restore();
    }

    ctx.restore();
}

function valueLabel(ctx: CanvasRenderingContext2D, x: number, y: number, text: string, color: string) {
    ctx.save();
    ctx.font = 'bold 10px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const w = ctx.measureText(text).width;
    ctx.fillStyle = 'rgba(0,0,0,0.55)';
    ctx.beginPath();
    ctx.roundRect(x - w / 2 - 3, y - 7, w + 6, 14, 2);
    ctx.fill();
    ctx.fillStyle = color;
    ctx.fillText(text, x, y);
    ctx.restore();
}

export function renderCompass(canvas: HTMLCanvasElement, config: CompassConfig): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return;
    }

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(cx, cy) - 43;

    if (radius <= 0) {
        return;
    }

    const colors = COLORS[config.theme];
    const compHeading = config.componentHeading ?? config.runwayHeading;

    ctx.clearRect(0, 0, w, h);

    // Ring
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.strokeStyle = colors.ring;
    ctx.lineWidth = 2;
    ctx.stroke();

    // Ticks + cardinal labels
    const cardinals: Record<number, string> = { 0: 'N', 90: 'E', 180: 'S', 270: 'W' };
    const cardinalLetterOffset: Record<number, number> = { 0: 28, 90: 28, 180: 27, 270: 34 };

    const hwColor = config.headwind >= 0 ? colors.headwind : colors.tailwind;
    const angDiff = (a: number, b: number) => { const d = ((a - b) % 360 + 360) % 360; return Math.min(d, 360 - d); };

    type RingMarker = { angle: number; color: string };
    const ringMarkers: RingMarker[] = [];
    if (config.showHeadingOnCompass) {
        const windAngle = (config.windDirection + 180) % 360;
        const hwAngle = config.headwind >= 0
            ? (compHeading + 180) % 360
            : ((compHeading % 360) + 360) % 360;
        const cwAngle = config.crosswind > 0
            ? (compHeading + 270) % 360
            : (compHeading + 90) % 360;
        ringMarkers.push({ angle: windAngle, color: colors.wind });
        ringMarkers.push({ angle: hwAngle, color: hwColor });
        if (Math.abs(config.crosswind) > 0.5) {
            ringMarkers.push({ angle: cwAngle, color: colors.crosswind });
        }
    }

    for (let deg = 0; deg < 360; deg += 10) {
        const rad = ((deg - 90) * Math.PI) / 180;
        const isCardinal = deg % 90 === 0;
        const tickLen = isCardinal ? 15 : deg % 30 === 0 ? 10 : 5;
        ctx.beginPath();
        ctx.moveTo(cx + (radius - tickLen) * Math.cos(rad), cy + (radius - tickLen) * Math.sin(rad));
        ctx.lineTo(cx + radius * Math.cos(rad), cy + radius * Math.sin(rad));
        ctx.strokeStyle = colors.tick;
        ctx.lineWidth = isCardinal ? 2 : 1;
        ctx.stroke();

        const suppressed = ringMarkers.some(m => angDiff(deg, m.angle) < 6);
        ctx.globalAlpha = suppressed ? 0.12 : 1;
        if (isCardinal) {
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.font = 'bold 11px sans-serif';
                ctx.fillStyle = colors.tick;
                ctx.fillText(deg === 0 ? '0°' : `${deg}°`, cx + (radius + 11) * Math.cos(rad), cy + (radius + 11) * Math.sin(rad));
                ctx.font = 'bold 14px sans-serif';
                ctx.fillStyle = colors.text;
                const letterOffset = radius + (cardinalLetterOffset[deg] ?? 24);
                ctx.fillText(cardinals[deg]!, cx + letterOffset * Math.cos(rad), cy + letterOffset * Math.sin(rad));
            } else if (deg % 30 === 0) {
                ctx.font = '9px sans-serif';
                ctx.fillStyle = colors.tick;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`${deg}°`, cx + (radius + 11) * Math.cos(rad), cy + (radius + 11) * Math.sin(rad));
            }
        ctx.globalAlpha = 1;
    }

    ringMarkers.forEach((marker, i) => {
        const mRad = ((marker.angle - 90) * Math.PI) / 180;
        ctx.beginPath();
        ctx.moveTo(cx + (radius - 9) * Math.cos(mRad), cy + (radius - 9) * Math.sin(mRad));
        ctx.lineTo(cx + (radius + 4) * Math.cos(mRad), cy + (radius + 4) * Math.sin(mRad));
        ctx.strokeStyle = marker.color;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        const minDist = ringMarkers.slice(0, i).reduce((min, prev) => Math.min(min, angDiff(marker.angle, prev.angle)), Infinity);
        ctx.globalAlpha = minDist < 7 ? 0.12 : 1;
        ctx.font = 'bold 10px sans-serif';
        ctx.fillStyle = marker.color;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${Math.round(marker.angle)}°`, cx + (radius + 13) * Math.cos(mRad), cy + (radius + 13) * Math.sin(mRad));
        ctx.globalAlpha = 1;
    });

    // Runway strips
    const compRad = ((compHeading - 90) * Math.PI) / 180;
    const rwyLen = radius - 22;
    const rwyW = 20;
    const magRunwayHeading = ((config.runwayHeading - (config.magneticVariation ?? 0)) % 360 + 360) % 360;
    const activeLabel = config.activeRunwayId ?? hdgToLabel(magRunwayHeading);
    const recipLabel = config.recipRunwayId ?? hdgToLabel(magRunwayHeading + 180);

    const axisSep = (a: number, b: number) => { const d = ((a - b) % 180 + 180) % 180; return Math.min(d, 180 - d); };
    const ghosts = (config.ghosts ?? []).filter(g => axisSep(g.bearing, config.runwayHeading) >= 1.5);

    const previewing = ghosts.some(g => g.isHovered);

    const ghostStyle = (ghost: CompassGhost): StripStyle => ({
        surface: colors.rwySurface,
        threshold: ghost.isBest ? colors.headwind : colors.rwySurface,
        label: colors.rwyLabel,
        recipLabel: colors.rwyLabel,
        alpha: 0.7,
        labelAlpha: 0.7,
        labelHalo: 'rgba(0,0,0,0.1)',
        outline: ghost.isHovered ? { color: colors.ring, dash: [4, 3] } : undefined
    });

    for (const ghost of ghosts.filter(g => !g.isHovered)) {
        drawRunwayStrip(ctx, cx, cy, ghost.bearing, rwyLen, rwyW, { active: ghost.label, recip: ghost.recipLabel }, ghostStyle(ghost));
    }

    drawRunwayStrip(ctx, cx, cy, config.runwayHeading, rwyLen, rwyW, { active: activeLabel, recip: recipLabel }, {
        surface: colors.rwySurface,
        threshold: colors.runwayActive,
        label: colors.rwyLabel,
        recipLabel: colors.rwyLabelDim,
        alpha: 1,
        // Dropped while previewing a ghost, so the accent follows whichever runway the arrows belong to.
        outline: previewing ? undefined : { color: colors.runwayActive }
    });

    // Hovered ghost sits on top — its components are the ones being previewed.
    for (const ghost of ghosts.filter(g => g.isHovered)) {
        drawRunwayStrip(ctx, cx, cy, ghost.bearing, rwyLen, rwyW, { active: ghost.label, recip: ghost.recipLabel }, ghostStyle(ghost));
    }

    const scaleLen = (v: number) => Math.min(Math.sqrt(Math.abs(v) / WIND_SCALE_MAX_KT) * radius * 0.92, radius * 0.86);

    // Arrows
    const windAngleRad = ((config.windDirection - compHeading) * Math.PI) / 180;

    const solidHW = config.showGustAsDashed ? config.windSpeed * Math.cos(windAngleRad) : config.headwind;
    const solidCW = config.showGustAsDashed ? config.windSpeed * Math.sin(windAngleRad) : config.crosswind;

    const hwEndX = solidHW >= 0 ? -scaleLen(solidHW) : scaleLen(solidHW);
    const cwEndY = solidCW > 0 ? -scaleLen(solidCW) : scaleLen(solidCW);

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(compRad);
    if (Math.abs(solidHW) > 0) {
        arrow(ctx, 0, 0, hwEndX, 0, hwColor);
    }
    if (Math.abs(solidCW) > 0) {
        arrow(ctx, 0, 0, 0, cwEndY, colors.crosswind);
    }

    if (config.showGustAsDashed && config.gustSpeed != null && config.gustSpeed > config.windSpeed) {
        const gustHW = config.gustSpeed * Math.cos(windAngleRad);
        const gustCW = config.gustSpeed * Math.sin(windAngleRad);

        ctx.setLineDash([4, 4]);
        ctx.lineCap = 'round';

        if (Math.abs(gustHW) > Math.abs(solidHW) + 0.5) {
            const gustHWEnd = gustHW >= 0 ? -scaleLen(gustHW) : scaleLen(gustHW);
            ctx.beginPath();
            ctx.moveTo(hwEndX, 0);
            ctx.lineTo(gustHWEnd, 0);
            ctx.strokeStyle = hwColor;
            ctx.lineWidth = ARROW_WIDTH * 0.7;
            ctx.stroke();
        }
        if (Math.abs(gustCW) > Math.abs(solidCW) + 0.5) {
            const gustCWEnd = gustCW > 0 ? -scaleLen(gustCW) : scaleLen(gustCW);
            ctx.beginPath();
            ctx.moveTo(0, cwEndY);
            ctx.lineTo(0, gustCWEnd);
            ctx.strokeStyle = colors.crosswind;
            ctx.lineWidth = ARROW_WIDTH * 0.7;
            ctx.stroke();
        }
        ctx.setLineDash([]);
    }
    ctx.restore();

    const effectiveSpeed = Math.sqrt(config.headwind ** 2 + config.crosswind ** 2);
    const windColor = colors.wind;
    const windRad = ((config.windDirection + 90) * Math.PI) / 180;
    const windLen = config.windArrowScaled !== false ? scaleLen(effectiveSpeed) : radius * 0.78;
    arrow(ctx, cx, cy, cx + windLen * Math.cos(windRad), cy + windLen * Math.sin(windRad), windColor, WIND_ARROW_WIDTH);

    // Center dot
    ctx.beginPath();
    ctx.arc(cx, cy, 4, 0, Math.PI * 2);
    ctx.fillStyle = colors.ring;
    ctx.fill();

    if (config.showValues) {
        const windTipX = cx + windLen * Math.cos(windRad);
        const windTipY = cy + windLen * Math.sin(windRad);
        valueLabel(ctx, windTipX - Math.sin(windRad) * 14, windTipY + Math.cos(windRad) * 14, `${config.windSpeed}kt`, windColor);

        if (Math.abs(config.headwind) > 0.5) {
            const tp = r2w(cx, cy, compRad, hwEndX, -(rwyW / 2 + 10));
            const lbl = config.headwind >= 0 ? `HW ${Math.abs(config.headwind)}kt` : `TW ${Math.abs(config.headwind)}kt`;
            valueLabel(ctx, tp.x, tp.y, lbl, hwColor);
        }
        if (Math.abs(config.crosswind) > 0.5) {
            const tp = r2w(cx, cy, compRad, 14, cwEndY);
            const lbl = config.crosswind > 0 ? `XW ${Math.abs(config.crosswind)}kt R` : `XW ${Math.abs(config.crosswind)}kt L`;
            valueLabel(ctx, tp.x, tp.y, lbl, colors.crosswind);
        }
    }
}
